// src/components/ZodiyakHelper.tsx hardened/refactored v1

import { useState, useEffect, useRef } from "react";
import { ZodiyakBubble } from "./ZodiyakBubble";
import { zodiyakTips, TipSection } from "@/utils/zodiyakTips";
import { ZodiacSign } from "@/utils/zodiac";

interface ZodiyakHelperProps {
  zodiacSign: ZodiacSign | null;
  currentSection: TipSection;
  isEnabled: boolean;
}

export function ZodiyakHelper({ zodiacSign, currentSection, isEnabled }: ZodiyakHelperProps) {
  const [isExpanded,    setIsExpanded]    = useState(true);
  const [currentSlide,  setCurrentSlide]  = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset to first slide and expand when section changes
  useEffect(() => {
    setCurrentSlide(0);
    setIsExpanded(true);
  }, [currentSection]);

  // Minimize when clicking outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setIsExpanded(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isEnabled) return null;

  const displaySign = zodiacSign || "Aquarius";
  const slides      = zodiyakTips[currentSection] || zodiyakTips.welcome;
  const totalSlides = slides.length;

  // Shared handler for both image and bubble clicks:
  // advance through slides in order, minimize after the last one
  const handleAdvanceOrMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextSlide = currentSlide + 1;
    if (nextSlide >= totalSlides) {
      setIsExpanded(false);
      setCurrentSlide(0); // reset so it starts fresh next time section opens
    } else {
      setCurrentSlide(nextSlide);
      setIsExpanded(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed z-50 flex items-start gap-3 animate-bounce-in"
      style={{ bottom: 20, left: 16 }}
    >
      <img
        src={`/SVG/zodiyak-${displaySign.toLowerCase()}.svg`}
        alt="Your Zodiyak helper"
        onClick={handleAdvanceOrMinimize}
        className={`object-contain transition-all duration-300 hover:scale-110 cursor-pointer ${
          isExpanded ? 'w-32 h-32' : 'w-16 h-16 mb-16'
        }`}
      />

      {isExpanded && (
        <div
          onClick={handleAdvanceOrMinimize}
          className="transition-opacity duration-300 cursor-pointer"
        >
          <ZodiyakBubble>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <p
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#1A1A1A",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {slides[currentSlide]}
              </p>
              {totalSlides > 1 && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <span
                    style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: 12,
                      color: "rgba(26,26,26,0.4)",
                      textAlign: "center",
                    }}
                  >
                    {currentSlide + 1}/{totalSlides}
                  </span>
                </div>
              )}
            </div>
          </ZodiyakBubble>
        </div>
      )}
    </div>
  );
}